/**
 * Copyright (c) Mayoral Moda Infantil, S.A.U. and affiliates.
 */

import { Notification, NotificationType } from '@components/ui/Notification';
import { createContext, FC, PropsWithChildren, useReducer } from 'react';

import { DEFAULT_SNACKBAR_DURATION } from './notification.utils';

export type NotificationData = {
  title: string;
  message?: string;
  type: NotificationType;
  duration?: number;
  onClose?: () => void;
};

export type NotificationState = {
  open: boolean;
  notification: NotificationData | null;
};

export enum ActionType {
  SHOW_NOTIFICATION = 'SHOW_NOTIFICATION',
  HIDE_NOTIFICATION = 'HIDE_NOTIFICATION',
}

type NotificationAction =
  | { type: ActionType.SHOW_NOTIFICATION; payload: NotificationData }
  | { type: ActionType.HIDE_NOTIFICATION };

const reducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case ActionType.SHOW_NOTIFICATION:
      return {
        ...state,
        open: true,
        notification: {
          ...action.payload,
          duration: action.payload.duration || DEFAULT_SNACKBAR_DURATION,
        },
      };
    case ActionType.HIDE_NOTIFICATION:
      return {
        ...state,
        open: false,
        notification: null,
      };
    default:
      return state;
  }
};

export type NotificationAPI = {
  showNotification: (notification: NotificationData) => void;
  hideNotification: () => void;
};

export const NotificationAPIContext = createContext<NotificationAPI>({} as NotificationAPI);

export const NotificationStateContext = createContext<NotificationState>({
  open: false,
  notification: null,
});

export const NotificationWrapper: FC<PropsWithChildren<object>> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, {
    open: false,
    notification: null,
  });

  const api: NotificationAPI = {
    showNotification: (notification: NotificationData) => {
      dispatch({ type: ActionType.SHOW_NOTIFICATION, payload: notification });
    },
    hideNotification: () => {
      dispatch({ type: ActionType.HIDE_NOTIFICATION });
    },
  };

  return (
    <NotificationAPIContext.Provider value={api}>
      <NotificationStateContext.Provider value={state}>
        <Notification />
        {children}
      </NotificationStateContext.Provider>
    </NotificationAPIContext.Provider>
  );
};

export default NotificationWrapper;
